import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, Filter, X, AlertTriangle, Camera, Upload, Shirt, Sparkles } from 'lucide-react';
import AIDetectDialog from '../components/AIDetectDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIZE_TYPES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Custom'];

const CONDITION_OPTIONS = [
  { value: 'good', label: 'Good', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  { value: 'fair', label: 'Fair', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  { value: 'needs_repair', label: 'Needs Repair', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
];

const Costumes = () => {
  const { getAuthHeaders, isManager, token } = useAuth();
  const [costumes, setCostumes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [wardrobes, setWardrobes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterWardrobe, setFilterWardrobe] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAIDetect, setShowAIDetect] = useState(false);
  const [selectedCostume, setSelectedCostume] = useState(null);
  const [similarItems, setSimilarItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', category_id: '', wardrobe_id: '',
    size_type: '', size_value: '', color: '', quantity: 1,
    condition: 'good', photo_path: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      if (filterWardrobe && filterWardrobe !== 'all') params.append('wardrobe_id', filterWardrobe);
      if (filterSize && filterSize !== 'all') params.append('size_type', filterSize);
      if (filterCondition && filterCondition !== 'all') params.append('condition', filterCondition);

      const [costumesRes, catsRes, wardrobesRes] = await Promise.all([
        axios.get(`${API_URL}/api/costumes?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/costume-categories`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/wardrobes`, { headers: getAuthHeaders() })
      ]);
      
      setCostumes(costumesRes.data);
      setCategories(catsRes.data);
      setWardrobes(wardrobesRes.data);
    } catch (error) {
      toast.error('Failed to fetch costumes');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, search, filterCategory, filterWardrobe, filterSize, filterCondition]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(debounce);
  }, [fetchData]);

  useEffect(() => {
    const debounce = setTimeout(async () => {
      if (formData.name && !selectedCostume && formData.name.length >= 3) {
        try {
          const resp = await axios.get(`${API_URL}/api/costumes/similar?name=${encodeURIComponent(formData.name)}`, { headers: getAuthHeaders() });
          setSimilarItems(resp.data.similar || []);
        } catch (e) { setSimilarItems([]); }
      } else {
        setSimilarItems([]);
      }
    }, 500);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line
  }, [formData.name]);

  const handleAIDetected = (detected) => {
    const matchedCategory = categories.find(c => 
      c.name.toLowerCase() === (detected.category_suggestion || '').toLowerCase()
    );
    
    setSelectedCostume(null);
    setPhotoUrl(null);
    setSimilarItems([]);
    setFormData({
      name: detected.name || '',
      description: detected.description || '',
      category_id: matchedCategory?.id || '',
      wardrobe_id: '',
      size_type: detected.size_type || '',
      size_value: detected.size_value || '',
      color: detected.color || '',
      quantity: 1,
      condition: detected.condition || 'good',
      photo_path: '',
      notes: ''
    });
    setShowDialog(true);
  };

  const handleOpenDialog = (costume = null) => {
    setSimilarItems([]);
    setPhotoUrl(null);
    if (costume) {
      setSelectedCostume(costume);
      setFormData({
        name: costume.name,
        description: costume.description || '',
        category_id: costume.category_id || '',
        wardrobe_id: costume.wardrobe_id || '',
        size_type: costume.size_type || '',
        size_value: costume.size_value || '',
        color: costume.color || '',
        quantity: costume.quantity,
        condition: costume.condition || 'good',
        photo_path: costume.photo_path || '',
        notes: costume.notes || ''
      });
      if (costume.photo_path) loadPhotoUrl(costume.photo_path);
    } else {
      setSelectedCostume(null);
      setFormData({
        name: '', description: '', category_id: '', wardrobe_id: '',
        size_type: '', size_value: '', color: '', quantity: 1,
        condition: 'good', photo_path: '', notes: ''
      });
    }
    setShowDialog(true);
  };

  const loadPhotoUrl = async (path) => {
    if (!path) return;
    try {
      const response = await axios.get(`${API_URL}/api/files/${path}?auth=${token}`, { responseType: 'blob' });
      setPhotoUrl(URL.createObjectURL(response.data));
    } catch (error) { console.error(error); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/api/upload/photo`, uploadData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, photo_path: response.data.path });
      await loadPhotoUrl(response.data.path);
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };
      if (selectedCostume) {
        await axios.put(`${API_URL}/api/costumes/${selectedCostume.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Costume updated');
      } else {
        await axios.post(`${API_URL}/api/costumes`, payload, { headers: getAuthHeaders() });
        toast.success('Costume created');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/costumes/${selectedCostume.id}`, { headers: getAuthHeaders() });
      toast.success('Costume deleted');
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const clearFilters = () => {
    setFilterCategory(''); setFilterWardrobe(''); setFilterSize(''); setFilterCondition('');
  };

  const hasFilters = filterCategory || filterWardrobe || filterSize || filterCondition;

  const getConditionBadge = (condition) => {
    const config = CONDITION_OPTIONS.find(c => c.value === condition) || CONDITION_OPTIONS[0];
    return <Badge variant="secondary" className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="costumes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Costumes</h1>
          <p className="text-muted-foreground mt-1">Manage your costume inventory</p>
        </div>
        {isManager() && (
          <div className="flex gap-2">
            <Button variant="outline" data-testid="ai-detect-costume-btn" onClick={() => setShowAIDetect(true)}>
              <Sparkles className="w-4 h-4 mr-2" />
              AI Detect
            </Button>
            <Button data-testid="add-costume-btn" onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Costume
            </Button>
          </div>
        )}
      </div>

      <Card className="border-border/50">
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, description, or color..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[140px]"><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterWardrobe} onValueChange={setFilterWardrobe}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Wardrobe" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Wardrobes</SelectItem>
                  {wardrobes.map((w) => <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterSize} onValueChange={setFilterSize}>
                <SelectTrigger className="w-[100px]"><SelectValue placeholder="Size" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sizes</SelectItem>
                  {SIZE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterCondition} onValueChange={setFilterCondition}>
                <SelectTrigger className="w-[130px]"><SelectValue placeholder="Condition" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {CONDITION_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {hasFilters && <Button variant="ghost" size="icon" onClick={clearFilters}><X className="w-4 h-4" /></Button>}
            </div>
          </div>
        </CardContent>
      </Card>

      {costumes.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="py-16 text-center">
            <Shirt className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No costumes yet. Add your first costume to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {costumes.map((costume) => (
            <CostumeCard 
              key={costume.id} 
              costume={costume} 
              token={token}
              onEdit={() => handleOpenDialog(costume)}
              onDelete={() => { setSelectedCostume(costume); setShowDeleteDialog(true); }}
              isManager={isManager()}
              getConditionBadge={getConditionBadge}
            />
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCostume ? 'Edit Costume' : 'Add Costume'}</DialogTitle>
            <DialogDescription>{selectedCostume ? 'Update costume details' : 'Add a new costume to the inventory'}</DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input
                data-testid="costume-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Victorian Ball Gown"
              />
              {similarItems.length > 0 && !selectedCostume && (
                <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Similar costumes found:</p>
                      <ul className="mt-1 text-sm text-muted-foreground space-y-1">
                        {similarItems.map((c) => (
                          <li key={c.id}>• {c.name} - Size {c.size_type} {c.size_value} (Qty: {c.quantity})</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the costume..."
                rows={2}
              />
            </div>

            {/* Photo */}
            <div className="grid gap-2">
              <Label>Photo</Label>
              <div className="flex items-center gap-3">
                {photoUrl ? (
                  <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-border">
                    <img src={photoUrl} alt="Costume" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                    <Camera className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" id="costume-photo" />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('costume-photo').click()} disabled={uploading}>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : (photoUrl ? 'Change' : 'Upload')}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Category</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Wardrobe</Label>
                <Select value={formData.wardrobe_id} onValueChange={(v) => setFormData({ ...formData, wardrobe_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select wardrobe" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Wardrobe</SelectItem>
                    {wardrobes.map((w) => <SelectItem key={w.id} value={w.id}>{w.label} - {w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Size (both type and value) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Size Type</Label>
                <Select value={formData.size_type} onValueChange={(v) => setFormData({ ...formData, size_type: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="e.g., M or Custom" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    {SIZE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Size Value / Numeric</Label>
                <Input
                  value={formData.size_value}
                  onChange={(e) => setFormData({ ...formData, size_value: e.target.value })}
                  placeholder="e.g., 12 or 32-34"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label>Colour</Label>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="e.g., Red"
                />
              </div>
              <div className="grid gap-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Condition</Label>
                <Select value={formData.condition} onValueChange={(v) => setFormData({ ...formData, condition: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITION_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button data-testid="save-costume-btn" onClick={handleSave} disabled={!formData.name}>
              {selectedCostume ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Costume</DialogTitle>
            <DialogDescription>Delete "{selectedCostume?.name}"? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Detect Dialog */}
      <AIDetectDialog
        open={showAIDetect}
        onClose={() => setShowAIDetect(false)}
        detectionType="costume"
        onDetected={handleAIDetected}
      />
    </div>
  );
};

const CostumeCard = ({ costume, token, onEdit, onDelete, isManager, getConditionBadge }) => {
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (costume.photo_path && token) {
      axios.get(`${API_URL}/api/files/${costume.photo_path}?auth=${token}`, { responseType: 'blob' })
        .then(response => setPhotoUrl(URL.createObjectURL(response.data)))
        .catch(console.error);
    }
    return () => { if (photoUrl) URL.revokeObjectURL(photoUrl); };
    // eslint-disable-next-line
  }, [costume.photo_path, token]);

  return (
    <Card className="border-border/50 overflow-hidden hover:border-primary/30 transition-colors" data-testid={`costume-card-${costume.id}`}>
      {photoUrl && (
        <div className="h-32 sm:h-40 bg-muted relative">
          <img src={photoUrl} alt={costume.name} className="w-full h-full object-cover" />
          <div className="absolute top-2 right-2">{getConditionBadge(costume.condition)}</div>
        </div>
      )}
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold truncate">{costume.name}</h3>
            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground flex-wrap">
              {costume.size_type && <span>{costume.size_type}</span>}
              {costume.size_value && <span>• {costume.size_value}</span>}
              {costume.color && <span>• {costume.color}</span>}
            </div>
          </div>
          {!photoUrl && (
            <div className="flex-shrink-0">{getConditionBadge(costume.condition)}</div>
          )}
        </div>
        
        <div className="flex items-center justify-between text-sm gap-2">
          <span className="text-muted-foreground">Qty: <strong className="text-foreground">{costume.quantity}</strong></span>
          {costume.wardrobe_label && (
            <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono truncate">{costume.wardrobe_label}</code>
          )}
        </div>

        {isManager && (
          <div className="flex gap-1 pt-2 border-t border-border">
            <Button variant="ghost" size="sm" className="flex-1" onClick={onEdit}>
              <Edit2 className="w-3 h-3 mr-1" />
              Edit
            </Button>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={onDelete}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Costumes;
